import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '@/state/store';
import type { SupportedLanguage } from '@/constants/supportedLanguages';
import { Tenant } from '@/hooks';

export type PrintFormat = 'a4' | 'thermal-80' | 'thermal-58';

interface PrinterPreferences {
  /** Default print format for thermal printing */
  default_thermal_print_format: 'thermal-58' | 'thermal-80';
  /** Auto print invoices using thermal printer */
  auto_print_invoices: boolean;
  /** Enable thermal printing instead of standard Android printing */
  enable_thermal_printing: boolean;
  /** Print density/darkness level (1-10, higher = darker) */
  print_density: number;
  /** Print quality mode for thermal printers */
  print_quality: 'normal' | 'high' | 'draft';
  /** Last connected printer MAC address for auto-reconnection */
  last_connected_printer_address?: string;
  /** Last connected printer name for display */
  last_connected_printer_name?: string;
  /** Current connected printer device info */
  current_connected_printer?: {
    address: string;
    name: string;
    id: string;
  };
  /** Current connection status */
  is_connected: boolean;
}

interface UserPreferenceState {
  printFormat: PrintFormat;
  appLanguage: SupportedLanguage; // Changed from currentLanguage to appLanguage for consistency
  showLanguageToggle: boolean;
  tenant: Tenant;
  printerPreferences: PrinterPreferences;
  /** Last visited tab in Online Store page - defaults to 'details' */
  lastOnlineStoreTab?: 'details' | 'orders';
}

const initialState: UserPreferenceState = {
  printFormat: 'thermal-80',
  appLanguage: 'english', // Changed from currentLanguage to appLanguage
  showLanguageToggle: false,
  tenant: Tenant.Buyer,
  printerPreferences: {
    default_thermal_print_format: 'thermal-80',
    auto_print_invoices: false,
    enable_thermal_printing: true, // Enabled by default
    print_density: 5,
    print_quality: 'normal',
    is_connected: false,
  },
};

const userPreferenceSlice = createSlice({
  name: 'userPreference',
  initialState,
  reducers: {
    setPrintFormat: (state, action: PayloadAction<PrintFormat>) => {
      state.printFormat = action.payload;
    },
    setAppLanguage: (state, action: PayloadAction<SupportedLanguage>) => {
      state.appLanguage = action.payload;
    },
    setShowLanguageToggle: (state, action: PayloadAction<boolean>) => {
      state.showLanguageToggle = action.payload;
    },
    setTenant: (state, action: PayloadAction<Tenant>) => {
      state.tenant = action.payload;
    },
    // Printer preference actions
    setDefaultThermalPrintFormat: (state, action: PayloadAction<'thermal-58' | 'thermal-80'>) => {
      state.printerPreferences.default_thermal_print_format = action.payload;
    },
    setAutoPrintInvoices: (state, action: PayloadAction<boolean>) => {
      state.printerPreferences.auto_print_invoices = action.payload;
    },
    setEnableThermalPrinting: (state, action: PayloadAction<boolean>) => {
      state.printerPreferences.enable_thermal_printing = action.payload;
    },
    setPrintDensity: (state, action: PayloadAction<number>) => {
      state.printerPreferences.print_density = action.payload;
    },
    setPrintQuality: (state, action: PayloadAction<'normal' | 'high' | 'draft'>) => {
      state.printerPreferences.print_quality = action.payload;
    },
    setLastConnectedPrinter: (state, action: PayloadAction<{ address: string; name: string }>) => {
      state.printerPreferences.last_connected_printer_address = action.payload.address;
      state.printerPreferences.last_connected_printer_name = action.payload.name;
    },
    clearLastConnectedPrinter: (state) => {
      delete state.printerPreferences.last_connected_printer_address;
      delete state.printerPreferences.last_connected_printer_name;
      delete state.printerPreferences.current_connected_printer;
      state.printerPreferences.is_connected = false;
    },
    setCurrentConnectedPrinter: (state, action: PayloadAction<{ address: string; name: string; id: string }>) => {
      state.printerPreferences.current_connected_printer = action.payload;
      state.printerPreferences.is_connected = true;
    },
    clearCurrentConnectedPrinter: (state) => {
      delete state.printerPreferences.current_connected_printer;
      state.printerPreferences.is_connected = false;
    },
    updateUserPreferences: (state, action: PayloadAction<Partial<UserPreferenceState>>) => {
      return { ...state, ...action.payload };
    },
    setLastOnlineStoreTab: (state, action: PayloadAction<'details' | 'orders'>) => {
      state.lastOnlineStoreTab = action.payload;
    },
    // Add more generic user preference actions here as needed
  },
});

export const {
  setPrintFormat,
  setAppLanguage,
  setShowLanguageToggle,
  setTenant,
  setDefaultThermalPrintFormat,
  setAutoPrintInvoices,
  setEnableThermalPrinting,
  setPrintDensity,
  setPrintQuality,
  setLastConnectedPrinter,
  clearLastConnectedPrinter,
  setCurrentConnectedPrinter,
  clearCurrentConnectedPrinter,
  updateUserPreferences,
  setLastOnlineStoreTab,
} = userPreferenceSlice.actions;
export default userPreferenceSlice.reducer;

// Selectors
export const selectAppLanguage = (state: RootState) => state.userPreference.appLanguage;
export const selectShowLanguageToggle = (state: RootState) => state.userPreference.showLanguageToggle;
export const selectTenant = (state: RootState) => state.userPreference.tenant;
export const selectPrinterPreferences = (state: RootState) => state.userPreference.printerPreferences;
export const selectUserPreferences = (state: RootState) => state.userPreference;
export const selectLastOnlineStoreTab = (state: RootState) => state.userPreference.lastOnlineStoreTab || 'details'; 